import { useState, useEffect } from 'react';
import {
    useLocation,
    useNavigate,
} from 'react-router-dom';
import styled from 'styled-components';
import {
    Card,
    Typography,
} from "@mui/material";

import { IconChevronLeft, IconTrash, IconKey, IconFileDollar } from '@tabler/icons-react';

import { useLicenseHook } from '../../Hooks/License/licenseHooks';

import { License } from '../../Models/License/License';

import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import RouteHelper from '../../Utilities/Helpers/RouteHelper';
import DateHelper from '../../Utilities/Helpers/DateHelper';
import StatusHelper from '../../Utilities/Helpers/StatusHelper';
import AccessibilityHelper from '../../Utilities/Helpers/AccessibilityHelper';
import GlobalFunctionHelper from '../../Utilities/Helpers/GlobalFunctionHelper';

import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { LicenseField, CompanyField, CompanyAddressField, DetailsInvoiceField } from '../../Utilities/Enums/ParametersEnum';
import { LicenseStatusEnum } from "../../Utilities/Enums/StatusEnums";
import { AdminModules } from '../../Utilities/Enums/AdminModulesEnum';
import { ValidityType } from '../../Utilities/Enums/ValidityTypeEnums';

import CustomButton from '../../CustomViews/Elements/CustomButton';
import CustomDetailsCard from '../../CustomViews/Pages/CustomDetailsCard/CustomDetailsCard';
import LicenseKeyTitle from '../../CustomViews/Pages/CustomTitles/LicenseKeyTitle';
import CustomExpirationDate from '../../CustomViews/Elements/CustomExpirationDate';
import IssueLicenseModal from './IssueLicenseModal';
import CustomDeleteConfirmation from '../../CustomViews/Elements/CustomDeleteConfirmation/CustomDeleteConfirmation';
import GeneratedLicenseInvoiceModal from './GeneratedLicenseInvoiceModal';
import LicenseModules from './LicenseModules';

const DetailsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    background: transparent !important;
    border: none;
    border-radius: 0px !important;
    display: grid;
    grid-template-columns: auto;
    gap: 30px;
    box-shadow: none !important;
    padding: 10px 0px;
`;

const FieldItemsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 30px;
`;

const FieldItemCard = styled(Card) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    width: 100%;
`;

const FieldItemName = styled(Typography) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    color: #878a99 !important;
    font-weight: 600 !important;
    font-size: 0.80rem !important;
    margin: 0px 0px 5px 0px !important;
    // margin: 0px !important;
`;

const FieldItemValue = styled(Typography) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    font-weight: 500 !important;
    font-size: 16px !important;
    word-break: break-all;
`;

const FieldItemValueContainer = styled.div <{ $darkMode?: boolean; }>`
    // line-height: 2.5;

    .MuiTypography-root.MuiTypography-caption.MuiTypography-gutterBottom {
        font-weight: 500 !important;
        font-size: 16px !important;
    }
`;

export default function LicenseDetails() {
    // Variables
    let navigate = useNavigate();
    let location = useLocation();
    let queryParams = new URLSearchParams(location.search);
    let licenseid = queryParams.get('id') ?? "0";

    // Use States
    const [queryStr, setQueryStr] = useState({
        pageSize: 10,
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        id: licenseid,
    });
    let [previewData, setpreviewData] = useState<License>(new License());
    let [openIssueLicenseModal, setopenIssueLicenseModal] = useState<boolean>(false);

    let [openDelete, setopenDelete] = useState(false);

    let [generatedInvoicePublicId, setgeneratedInvoicePublicId] = useState<any>(null);
    let [generatedInvoiceNo, setgeneratedInvoiceNo] = useState<any>(null);
    let [openGeneratedInvoiceModal, setopenGeneratedInvoiceModal] = useState<boolean>(false);

    const q_queryKey = [CachingEnum.LICENSES, queryStr]
    const { getLicenseById, generateLicenseInvoiceById, deleteLicenseById } = useLicenseHook(q_queryKey, queryStr, false);

    //Event Handlers
    const getCompanyData = async () => {
        if(licenseid != '0') {
            
            let getDetails = await getLicenseById(licenseid);

            setpreviewData(getDetails?.data ?? new License());
        }
    }

    useEffect(() => {
            getCompanyData();
    }, [licenseid]);
    
    //Rendering Functions
    const renderActionButtons = () => {
        return <>
            <CustomButton
                btnType={ButtonType.CANCEL}
                title='Back'
                startIcon={<IconChevronLeft />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    GlobalFunctionHelper.goBack(navigate, location, RouteEnum.LICENSES);
                }} />
            {AccessibilityHelper.hasAccess(AdminModules.LICENSE_DELETE) && previewData?.[LicenseField.STATUS] != LicenseStatusEnum.USED && <CustomButton
                btnType={ButtonType.DELETE}
                title='Delete'
                startIcon={<IconTrash />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    setopenDelete(true);
                }} />}
            {AccessibilityHelper.hasAccess(AdminModules.BILLING_CREATE) && !previewData?.[LicenseField.INVOICE_EXISTS] && previewData?.[LicenseField.COMPANY_PUBLIC_ID] && <CustomButton
                btnType={ButtonType.PRIMARY}
                title='Generate Invoice'
                startIcon={<IconFileDollar />}
                // isRoundedCorner={true}
                onClick={async (e: any) => {
                    let generateInvoiceResponse = await generateLicenseInvoiceById(previewData?.[LicenseField.PUBLIC_ID]);

                    if(generateInvoiceResponse?.isSuccess) {
                        setgeneratedInvoicePublicId(generateInvoiceResponse?.data?.data?.[DetailsInvoiceField.PUBLIC_ID] ?? null);
                        setgeneratedInvoiceNo(generateInvoiceResponse?.data?.data?.[DetailsInvoiceField.TRANS_NO] ?? null);
                        setopenGeneratedInvoiceModal(true);
                    }
                }} />}
        </>
    }

    const renderLicenseDetails = () => {
        return <CustomDetailsCard
            title={RouteHelper.getRouteDescription(RouteEnum.LICENSE_DETAILS)}
            actionButtons={renderActionButtons()} >
                <FieldItemsContainer>
                    <FieldItemCard>
                        <FieldItemName>License Key</FieldItemName>
                        {!previewData?.[LicenseField.LICENSE_KEY] && <FieldItemValue>--</FieldItemValue>}
                        {previewData?.[LicenseField.LICENSE_KEY] && <FieldItemValueContainer><LicenseKeyTitle licenseKey={previewData?.[LicenseField.LICENSE_KEY] ?? ""} color="inherit" /></FieldItemValueContainer>}
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>Data Storage</FieldItemName>
                        <FieldItemValue>{previewData?.[LicenseField.STORAGE_CAPACITY] ?? "0"} GB</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>No. of Users</FieldItemName>
                        <FieldItemValue>{previewData?.[LicenseField.USER_CAPACITY] ?? "0"} Users</FieldItemValue>
                    </FieldItemCard>

                    <FieldItemCard>
                        <FieldItemName>Validity</FieldItemName>
                        <FieldItemValue>{(previewData?.[LicenseField.VALIDITY] ?? "0") + " " + (ValidityType?.[previewData?.[LicenseField.VALIDITY_TYPE]]?.title ?? "Month") + "(s)"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>License Issued Date</FieldItemName>
                        <FieldItemValue>{previewData?.[LicenseField.ISSUE_DATE] ? DateHelper.getFormattedDate(previewData?.[LicenseField.ISSUE_DATE], "dd/MM/yyyy") : "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>License Expiry Date</FieldItemName>
                        {!previewData?.[LicenseField.EXPIRY_DATE] && <FieldItemValue>--</FieldItemValue>}
                        {previewData?.[LicenseField.EXPIRY_DATE] && <FieldItemValueContainer><CustomExpirationDate isFromDetails={true} expiryDate={previewData?.[LicenseField.EXPIRY_DATE] ?? null} /></FieldItemValueContainer>}
                    </FieldItemCard>

                    {/*<FieldItemCard>
                        <FieldItemName>Modules</FieldItemName>
                        <FieldItemValue>{previewData?.[LicenseField.MODULES]?.map((item: any) => { return item.name })?.toString() ?? "--"}</FieldItemValue>
                    </FieldItemCard>*/}
                    <FieldItemCard>
                        <FieldItemName>Generated/Purchased On</FieldItemName>
                        <FieldItemValue>{previewData?.[LicenseField.CREATED_DATE] ? DateHelper.getFormattedDate(previewData?.[LicenseField.CREATED_DATE], "dd/MM/yyyy hh:mm a") : "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>Status</FieldItemName>
                        {previewData?.[LicenseField.STATUS] == LicenseStatusEnum.ALL && <FieldItemValue>--</FieldItemValue>}
                        {previewData?.[LicenseField.STATUS] != LicenseStatusEnum.ALL && <FieldItemValueContainer>{StatusHelper.getLicenseStatus(previewData?.[LicenseField.STATUS] ?? LicenseStatusEnum.ALL)}</FieldItemValueContainer>}
                    </FieldItemCard>

                    
                    {/*<FieldItemCard>
                        <FieldItemName>{contPersonProps?.title ?? "--"}</FieldItemName>
                        <FieldItemValue>{previewData?.[CompanyField.CONTACT_PERSON] ?? "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>Created Date</FieldItemName>
                        <FieldItemValue>{previewData?.[CompanyField.CREATED_DATE] ? DateHelper.getFormattedDate(previewData?.[CompanyField.CREATED_DATE], "dd/MM/yyyy hh:mm a") : "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>{contEmailAddressProps?.title ?? "--"}</FieldItemName>
                        <FieldItemValue>{previewData?.[CompanyField.EMAIL] ?? "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>{contPhoneProps?.title ?? "--"}</FieldItemName>
                        <FieldItemValue>{previewData?.[CompanyField.CONTACT_PHONE] ?? "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>Last Updated</FieldItemName>
                        <FieldItemValue>{previewData?.[CompanyField.UPDATED_DATE] ? DateHelper.getFormattedDate(previewData?.[CompanyField.UPDATED_DATE], "dd/MM/yyyy hh:mm a") : "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>{billingAddressProps?.title ?? "--"}</FieldItemName>
                        <FieldItemValue>{previewData?.[CompanyField.ADDRESS]?.[CompanyAddressField.FULL_ADDRESS] ?? "--"}</FieldItemValue>
                    </FieldItemCard>*/}
                </FieldItemsContainer>
            </CustomDetailsCard>
    }
    const renderIssuedCompany = () => {
        return <CustomDetailsCard title="Issued Company">
            <FieldItemsContainer>
                {previewData?.[LicenseField.COMPANY_PUBLIC_ID] && <>
                    <FieldItemCard>
                        <FieldItemName>Company Name</FieldItemName>
                        <FieldItemValue>{previewData?.[LicenseField.COMPANY_NAME] ?? "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>Contact Person</FieldItemName>
                        <FieldItemValue>{previewData?.[LicenseField.COMPANY]?.[CompanyField.CONTACT_PERSON] ?? "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>Contact Number</FieldItemName>
                        <FieldItemValue>{previewData?.[LicenseField.COMPANY]?.[CompanyField.CONTACT_PHONE] ?? "--"}</FieldItemValue>
                    </FieldItemCard>

                    <FieldItemCard>
                        <FieldItemName>Email Address</FieldItemName>
                        <FieldItemValue>{previewData?.[LicenseField.COMPANY]?.[CompanyField.EMAIL] ?? "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>Billing Address</FieldItemName>
                        <FieldItemValue>{previewData?.[LicenseField.COMPANY]?.[CompanyField.ADDRESS]?.[CompanyAddressField.FULL_ADDRESS] ?? "--"}</FieldItemValue>
                    </FieldItemCard>
                </>}

                {!previewData?.[LicenseField.COMPANY_PUBLIC_ID] && <>
                    <FieldItemCard>
                        <FieldItemName>Company Name</FieldItemName>
                        <FieldItemValueContainer>
                            <CustomButton
                                btnType={ButtonType.PRIMARY}
                                title='Issue License'
                                startIcon={<IconKey />}
                                onClick={(e: any) => {
                                    setopenIssueLicenseModal(true);
                                }} />
                        </FieldItemValueContainer>
                    </FieldItemCard>
                </>}
            </FieldItemsContainer>
        </CustomDetailsCard>
    }

    const renderLicenseModules = () => {
        return <LicenseModules modules={previewData?.[LicenseField.MODULES] ?? []} />
    }

    return <DetailsContainer>
        {renderLicenseDetails()}
        {renderLicenseModules()}
        {renderIssuedCompany()}

        {openIssueLicenseModal && <IssueLicenseModal
            openIssueLicenseModal={openIssueLicenseModal}
            setopenIssueLicenseModal={setopenIssueLicenseModal}
            licensePublicId={licenseid}
            getCompanyData={getCompanyData} />}

        {openDelete && <CustomDeleteConfirmation
            openDialog={openDelete}
            ItemToDelete={previewData?.[LicenseField.LICENSE_KEY] ?? null} // Optional to display name of the item to be deleted
            handleClose={() => {
                setopenDelete(false);
            }}
            showRemarksField={true}
            deleteItem={async (remarks: any) => {
                if(previewData?.[LicenseField.PUBLIC_ID]) {
                    let deleteReponse: any = await deleteLicenseById(previewData?.[LicenseField.PUBLIC_ID], remarks);

                    if(deleteReponse?.isSuccess) {
                        setopenDelete(false);

                        navigate(RouteEnum.LICENSES, { replace: true });
                    }
                }
            }} />
        }

        {openGeneratedInvoiceModal && <GeneratedLicenseInvoiceModal
            openGeneratedInvoiceModal={openGeneratedInvoiceModal}
            setopenGeneratedInvoiceModal={setopenGeneratedInvoiceModal}
            generatedInvoicePublicId={generatedInvoicePublicId}
            setgeneratedInvoicePublicId={setgeneratedInvoicePublicId}
            generatedInvoiceNo={generatedInvoiceNo}
            setgeneratedInvoiceNo={setgeneratedInvoiceNo} />
        }
    </DetailsContainer>
}
